import type { Action, ThunkAction, UnknownAction } from '@reduxjs/toolkit';
import { combineReducers, configureStore } from '@reduxjs/toolkit';
import storageSession from 'redux-persist/lib/storage/session';
import {
  persistStore,
  persistReducer,
  REHYDRATE,
  PAUSE,
  FLUSH,
  PERSIST,
  PURGE,
  REGISTER,
} from 'redux-persist';
import * as Sentry from '@sentry/react';

// slices
import apiSlice from 'services/api/apiSlice';
import alertsSlice from 'services/alerts/alertsSlice';
import userSlice from 'services/user/userSlice';
import sessionSlice from 'services/session/sessionSlice';
import watchlistSlice from 'services/watchlist/watchlistSlice';
import enrollmentSlice from 'services/enrollment/enrollmentSlice';
import offerSlice from 'services/offer/offerSlice';
import dbooSlice from 'services/dboo/dbooSlice';
import vaultSlice from 'services/vault/vaultSlice';
import brandSlice from 'services/brand/brandSlice';
import toastAlertSlice from 'services/toast-alert/toastAlertSlice';
import planSlice from 'services/plan/planSlice';
import criticalNotificationsSlice from 'services/misc/criticalNotificationSlice';
import miscSlice from 'services/misc/miscSlice';
import secureUploadSlice from 'services/secureUpload/secureUploadSlice';
import securitySlice from 'services/security/securitySlice';
import ctpSlice from 'services/ctp/ctpSlice';
import htmonSlice from 'services/htmon/htmonSlice';

export const persistConfig = {
  key: 'root',
  storage: storageSession,
  version: 1,
  whitelist: [
    'session',
    'user',
    'alerts',
    // 'watchlist',
    // 'enrollment',
    'offer',
    // 'dboo',
    'brand',
    'criticalNotifications',
    'misc',
    'ctp',
    'plan',
    'htmon',
  ], // persisted reducers
};

const appReducer = combineReducers({
  enrollment: enrollmentSlice,
  api: apiSlice,
  alerts: alertsSlice,
  user: userSlice,
  session: sessionSlice,
  watchlist: watchlistSlice,
  offer: offerSlice,
  plan: planSlice,
  dboo: dbooSlice,
  vault: vaultSlice,
  brand: brandSlice,
  toastAlert: toastAlertSlice,
  criticalNotifications: criticalNotificationsSlice,
  misc: miscSlice,
  secureUpload: secureUploadSlice,
  security: securitySlice,
  ctp: ctpSlice,
  htmon: htmonSlice,
});

const sentryReduxEnhancer = Sentry.createReduxEnhancer({});

const rootReducer = (state: any, action: UnknownAction) => {
  if (action.type === 'SIGNOUT_REQUEST') {
    storageSession.removeItem('persist:root');
    // reducers are supposed to return the initial state when they are called with undefined as the first argument, no matter the action.
    return appReducer(undefined, action);
  }

  return appReducer(state, action);
};

// Infer the `RootState` type from the root reducer
export type RootState = ReturnType<typeof rootReducer>;

const persistedReducer = persistReducer(persistConfig, rootReducer);

// The store setup is wrapped in `makeStore` to allow reuse
// when setting up tests that need the same store config
export const makeStore = () => {
  const store = configureStore({
    reducer: persistedReducer,
    devTools: import.meta.env.VITE_PS_ENV !== 'prod',
    // Adding the api middleware enables caching, invalidation, polling,
    // and other useful features of `rtk-query`.
    // middleware: (getDefaultMiddleware) => {
    //   return getDefaultMiddleware().concat(quotesApiSlice.middleware);
    // },
    // preloadedState,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: {
          ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
        },
      }),
    enhancers: (getDefaultEnhancers) => {
      return getDefaultEnhancers().concat(sentryReduxEnhancer);
    },
  });
  // configure listeners using the provided defaults
  // optional, but required for `refetchOnFocus`/`refetchOnReconnect` behaviors
  return store;
};

export const store = makeStore();

export const persistor = persistStore(store);

// Infer the type of `store`
export type AppStore = typeof store;
// Infer the `AppDispatch` type from the store itself
export type AppDispatch = AppStore['dispatch'];
export type AppState = AppStore['getState'];
export type AppThunk<ThunkReturnType = void> = ThunkAction<
  ThunkReturnType,
  RootState,
  unknown,
  Action
>;
